import React, { useState } from 'react';
import { useDrawer } from '../../contexts/AuthContext';
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Typography,
  Button,
  Stack,
  Chip,
  Popover,
  TextField,
  Grid,
  Paper,
  Menu,
} from '@mui/material';
import {
  Highlight as HighlightIcon,
  ShapeLine as ShapeIcon,
  TextFields as TextIcon,
  Edit as FreehandIcon,
  Undo as UndoIcon,
  Save as SaveIcon,
  FormatColorFill as ColorIcon,
  LineWeight as WeightIcon,
  Menu as MenuIcon,
  FormatSize as FontSizeIcon,
  FontDownload as FontTypeIcon,
  ShowChart as LineTypeIcon,
  ExpandMore as ExpandMoreIcon,
  OpenWith as MoveIcon,
  Rectangle as RectangleIcon,
  Lens as EllipseIcon,
  ArrowBack as PreviousIcon,
  ArrowForward as NextIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as ZoomResetIcon,
} from '@mui/icons-material';
import Download from '@mui/icons-material/Download';
import Print from '@mui/icons-material/Print';
import { AnnotationType, ShapeType, LineType, AnnotationStyle } from './types/AnnotationTypes';

// Props del componente
interface AnnotationToolbarProps {
  onToolChange: (tool: AnnotationType | null) => void;
  onStyleChange: (style: AnnotationStyle) => void;
  onUndo: () => void;
  onSave: () => void;
  currentTool: AnnotationType | null;
  currentStyle: AnnotationStyle;
  canUndo: boolean;
  selectedShape?: ShapeType;
  onShapeChange?: (shape: ShapeType) => void;
  currentPage?: number;
  totalPages?: number;
  handlePreviousPage?: () => void;
  handleNextPage?: () => void;
  handlePageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isPdf?: boolean;
  onDownloadWithAnnotations?: () => void;
  onDownloadWithoutAnnotations?: () => void;
  onPrintWithAnnotations?: () => void;
  onPrintWithoutAnnotations?: () => void;
  annotationsVisible?: boolean;
  onToggleAnnotationsVisibility?: () => void;
  // Zoom controls
  zoom?: number;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onZoomReset?: () => void;
  onZoomChange?: (zoom: number) => void;
  minZoom?: number;
  maxZoom?: number;
  // Preview mode
  isPreview?: boolean;
  isAdmin?: boolean;
}

const AnnotationToolbar: React.FC<AnnotationToolbarProps> = ({
  onToolChange,
  onStyleChange,
  onUndo,
  onSave,
  currentTool,
  currentStyle,
  canUndo,
  selectedShape = 'rectangle',
  onShapeChange,
  currentPage = 1,
  totalPages = 1,
  handlePreviousPage,
  handleNextPage,
  handlePageChange,
  isPdf = false,
  onDownloadWithAnnotations,
  onDownloadWithoutAnnotations,
  onPrintWithAnnotations,
  onPrintWithoutAnnotations,
  annotationsVisible = true,
  onToggleAnnotationsVisibility,
  // Zoom controls
  zoom = 1.5,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onZoomChange,
  minZoom = 0.5,
  maxZoom = 3.0,
  // Preview mode
  isPreview = false,
  isAdmin = false,
}) => {
  const { openDrawer } = useDrawer();
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
  const [thicknessAnchor, setThicknessAnchor] = useState<HTMLElement | null>(null);
  const [fontSizeAnchor, setFontSizeAnchor] = useState<HTMLElement | null>(null);
  const [lineTypeAnchor, setLineTypeAnchor] = useState<HTMLElement | null>(null);
  const [shapePickerAnchor, setShapePickerAnchor] = useState<HTMLElement | null>(null);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  // Extended color palette
  const colorOptions = [
    // Reds
    '#FF0000', '#FF4444', '#FF6666', '#FF8888', '#FFAAAA', '#FFCCCC',
    // Oranges
    '#FF8000', '#FFA040', '#FFB366', '#FFC680', '#FFD699', '#FFE6B3',
    // Yellows
    '#FFFF00', '#FFFF40', '#FFFF66', '#FFFF88', '#FFFFAA', '#FFFFCC',
    // Greens
    '#00FF00', '#40FF40', '#66FF66', '#88FF88', '#AAFFAA', '#CCFFCC',
    // Blues
    '#0000FF', '#4444FF', '#6666FF', '#8888FF', '#AAAAFF', '#CCCCFF',
    // Purples
    '#8000FF', '#A040FF', '#B366FF', '#C680FF', '#D699FF', '#E6B3FF',
    // Pinks
    '#FF00FF', '#FF40FF', '#FF66FF', '#FF88FF', '#FFAAFF', '#FFCCFF',
    // Grays
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
  ];

  // Font options
  const fontOptions = [
    'Arial', 'Times New Roman', 'Courier New', 'Verdana', 'Georgia', 'Comic Sans MS'
  ];

  // Shape options
  const shapeOptions: { value: ShapeType; label: string; icon: React.ReactNode }[] = [
    { value: 'rectangle', label: 'Rectangle', icon: <RectangleIcon /> },
    { value: 'oval', label: 'Oval', icon: <EllipseIcon /> },
  ];

  // Line types with examples
  const lineTypeOptions: { value: LineType; label: string; example: string }[] = [
    { value: 'solid', label: 'Solid', example: '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━' },
    { value: 'dashed', label: 'Dashed', example: '┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅' },
    { value: 'dotted', label: 'Dotted', example: '┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈' },
  ];

  // Thickness presets
  const thicknessPresets = [1, 2, 3, 5, 8, 12, 16, 20];

  // Font size presets
  const fontSizePresets = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72];

  // Handle tool change
  const handleToolChange = (tool: AnnotationType | null) => {
    if (currentTool === tool) {
      onToolChange(null); // Deactivate if already active
    } else {
      onToolChange(tool);
    }
  };

  // Handle style change
  const handleStyleChange = (property: keyof AnnotationStyle, value: any) => {
    onStyleChange({
      ...currentStyle,
      [property]: value,
    });
  };

  // Color picker handlers
  const handleColorClick = (event: React.MouseEvent<HTMLElement>) => {
    setColorPickerAnchor(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorPickerAnchor(null);
  };

  const handleColorSelect = (color: string) => {
    handleStyleChange('color', color);
    handleColorClose();
  };

  // Thickness picker handlers
  const handleThicknessClick = (event: React.MouseEvent<HTMLElement>) => {
    setThicknessAnchor(event.currentTarget);
  };

  const handleThicknessClose = () => {
    setThicknessAnchor(null);
  };

  const handleThicknessSelect = (thickness: number) => {
    handleStyleChange('thickness', thickness);
    handleThicknessClose();
  };

  // Font size picker handlers
  const handleFontSizeClick = (event: React.MouseEvent<HTMLElement>) => {
    setFontSizeAnchor(event.currentTarget);
  };

  const handleFontSizeClose = () => {
    setFontSizeAnchor(null);
  };

  const handleFontSizeSelect = (size: number) => {
    handleStyleChange('fontSize', size);
    handleFontSizeClose();
  };

  // Shape picker handlers
  const handleShapeClick = (event: React.MouseEvent<HTMLElement>) => {
    setShapePickerAnchor(event.currentTarget);
  };

  const handleShapeClose = () => {
    setShapePickerAnchor(null);
  };

  const handleShapeSelect = (shape: ShapeType) => {
    if (onShapeChange) {
      onShapeChange(shape);
    }
    handleShapeClose();
  };

  // Line type picker handlers
  const handleLineTypeClick = (event: React.MouseEvent<HTMLElement>) => {
    setLineTypeAnchor(event.currentTarget);
  };

  const handleLineTypeClose = () => {
    setLineTypeAnchor(null);
  };

  const handleLineTypeSelect = (lineType: LineType) => {
    handleStyleChange('lineType', lineType);
    handleLineTypeClose();
  };

  // Export handlers
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchor(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportAnchor(null);
  };

  // Helper function to determine which controls to show
  const shouldShowControl = (controlType: 'color' | 'thickness' | 'fontSize' | 'fontType' | 'lineType') => {
    switch (currentTool) {
      case 'text':
        return ['color', 'fontSize', 'fontType'].includes(controlType);
      case 'highlight':
        return ['color'].includes(controlType);
      case 'shapes':
        return ['color', 'thickness', 'lineType'].includes(controlType);
      case 'freehand':
        return ['color', 'thickness'].includes(controlType);
      default:
        return false;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        gap: 2,
        flexWrap: 'wrap',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'relative',
      }}
    >
      {/* Herramientas a la izquierda */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
        
        {/* Botón del menú lateral - SIEMPRE VISIBLE */}
        <Tooltip title="Open Menu">
          <IconButton
            size="small"
            onClick={openDrawer}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {annotationsVisible && (
          <>
            <Tooltip title="Move">
              <IconButton
                size="small"
                onClick={() => handleToolChange('move')}
                sx={{
                  backgroundColor: currentTool === 'move' ? 'primary.main' : 'transparent',
                  color: currentTool === 'move' ? 'white' : 'inherit',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: currentTool === 'move' ? 'primary.dark' : 'action.hover',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <MoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Highlight">
              <IconButton
                size="small"
                onClick={() => handleToolChange('highlight')}
                sx={{
                  backgroundColor: currentTool === 'highlight' ? 'primary.main' : 'transparent',
                  color: currentTool === 'highlight' ? 'white' : 'inherit',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: currentTool === 'highlight' ? 'primary.dark' : 'action.hover',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <HighlightIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Shapes">
              <IconButton
                size="small"
                onClick={() => handleToolChange('shapes')}
                sx={{
                  backgroundColor: currentTool === 'shapes' ? 'primary.main' : 'transparent',
                  color: currentTool === 'shapes' ? 'white' : 'inherit',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: currentTool === 'shapes' ? 'primary.dark' : 'action.hover',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ShapeIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Text">
              <IconButton
                size="small"
                onClick={() => handleToolChange('text')}
                sx={{
                  backgroundColor: currentTool === 'text' ? 'primary.main' : 'transparent',
                  color: currentTool === 'text' ? 'white' : 'inherit',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: currentTool === 'text' ? 'primary.dark' : 'action.hover',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <TextIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Freehand">
              <IconButton
                size="small"
                onClick={() => handleToolChange('freehand')}
                sx={{
                  backgroundColor: currentTool === 'freehand' ? 'primary.main' : 'transparent',
                  color: currentTool === 'freehand' ? 'white' : 'inherit',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: currentTool === 'freehand' ? 'primary.dark' : 'action.hover',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <FreehandIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Botón de toggle de anotaciones */}
        {onToggleAnnotationsVisibility && (
          <>
            <Divider orientation="vertical" flexItem sx={{ height: 32 }} />
            <Tooltip title={annotationsVisible ? "Hide Annotations" : "Show Annotations"}>
              <IconButton
                size="small"
                onClick={onToggleAnnotationsVisibility}
                sx={{
                  backgroundColor: annotationsVisible ? 'success.main' : 'error.main',
                  color: 'white',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: annotationsVisible ? 'success.dark' : 'error.dark',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {annotationsVisible ? (
                  <VisibilityIcon fontSize="small" />
                ) : (
                  <VisibilityOffIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </>
        )}

      </Box>
      {/* Selector de página a la derecha */}
      {isPdf && totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 'auto' }}>
          <Tooltip title="Previous page">
            <IconButton
              size="small"
              onClick={handlePreviousPage}
              disabled={currentPage <= 1}
              sx={{ color: currentPage <= 1 ? 'action.disabled' : 'primary.main' }}
            >
              <PreviousIcon />
            </IconButton>
          </Tooltip>
          <TextField
            size="small"
            value={currentPage}
            onChange={handlePageChange}
            sx={{
              width: 60,
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                textAlign: 'center',
              },
            }}
          />
          <Typography variant="body2" color="text.secondary">
            / {totalPages}
          </Typography>
          <Tooltip title="Next page">
            <IconButton
              size="small"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              sx={{ color: currentPage >= totalPages ? 'action.disabled' : 'primary.main' }}
            >
              <NextIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Controles de Zoom */}
      {isPdf && (
        <>
          <Divider orientation="vertical" flexItem sx={{ height: 32 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Zoom Out">
              <IconButton
                size="small"
                onClick={onZoomOut}
                disabled={zoom <= minZoom}
                sx={{ color: zoom <= minZoom ? 'action.disabled' : 'primary.main' }}
              >
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 80 }}>
              <Slider
                size="small"
                value={zoom}
                onChange={(_, value) => onZoomChange?.(value as number)}
                min={minZoom}
                max={maxZoom}
                step={0.1}
                sx={{ width: 60 }}
              />
              <Typography variant="body2" sx={{ minWidth: 45, textAlign: 'center' }}>
                {Math.round(zoom * 100)}%
              </Typography>
            </Box>
            
            <Tooltip title="Zoom In">
              <IconButton
                size="small"
                onClick={onZoomIn}
                disabled={zoom >= maxZoom}
                sx={{ color: zoom >= maxZoom ? 'action.disabled' : 'primary.main' }}
              >
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Reset Zoom">
              <IconButton
                size="small"
                onClick={onZoomReset}
                sx={{ color: 'primary.main' }}
              >
                <ZoomResetIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {annotationsVisible && (
        <>
          <Divider orientation="vertical" flexItem sx={{ height: 32 }} />

          {/* Style Controls Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        
        {/* Color Picker - Always visible */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Color">
            <IconButton
              size="small"
              onClick={handleColorClick}
              sx={{
                backgroundColor: currentStyle.color,
                border: '2px solid #e0e0e0',
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ColorIcon fontSize="small" sx={{ color: '#fff', filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Thickness Picker - Only for highlight, shapes, freehand */}
        {shouldShowControl('thickness') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Thickness">
              <Box
                onClick={handleThicknessClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '6px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#e8e8e8',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <WeightIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 20 }}>
                  {currentStyle.thickness}
                </Typography>
                <ExpandMoreIcon fontSize="small" color="action" />
              </Box>
            </Tooltip>
          </Box>
        )}

        {/* Font Size Picker - Only for text */}
        {shouldShowControl('fontSize') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Font Size">
              <Box
                onClick={handleFontSizeClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '6px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#e8e8e8',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <FontSizeIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 20 }}>
                  {currentStyle.fontSize}
                </Typography>
                <ExpandMoreIcon fontSize="small" color="action" />
              </Box>
            </Tooltip>
          </Box>
        )}

        {/* Font Type - Only for text */}
        {shouldShowControl('fontType') && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Font</InputLabel>
            <Select
              value={currentStyle.fontFamily}
              onChange={(e) => handleStyleChange('fontFamily', e.target.value)}
              label="Font"
              sx={{
                borderRadius: '6px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
              }}
            >
              {fontOptions.map((font) => (
                <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                  {font}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Line Type Picker - Only for shapes */}
        {shouldShowControl('lineType') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Line Type">
              <Box
                onClick={handleLineTypeClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '6px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#e8e8e8',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <LineTypeIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 40 }}>
                  {lineTypeOptions.find(opt => opt.value === currentStyle.lineType)?.label}
                </Typography>
                <ExpandMoreIcon fontSize="small" color="action" />
              </Box>
            </Tooltip>
          </Box>
        )}

        {/* Shape Picker - Only when shapes tool is selected */}
        {currentTool === 'shapes' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Shape Type">
              <Box
                onClick={handleShapeClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '6px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#e8e8e8',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {shapeOptions.find(opt => opt.value === selectedShape)?.icon}
                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 60 }}>
                  {shapeOptions.find(opt => opt.value === selectedShape)?.label}
                </Typography>
                <ExpandMoreIcon fontSize="small" color="action" />
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>
        </>
      )}

      <Divider orientation="vertical" flexItem sx={{ height: 32 }} />

      {/* Action Buttons Section */}
      {annotationsVisible && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Undo">
            <IconButton
              size="small"
              onClick={onUndo}
              disabled={!canUndo}
              sx={{
                color: canUndo ? 'inherit' : 'action.disabled',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: canUndo ? 'action.hover' : 'transparent',
                  transform: canUndo ? 'scale(1.05)' : 'none',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Botón Save */}
          <Tooltip title="Save all annotations to server">
            <span>
              <IconButton
                size="small"
                onClick={onSave}
                sx={{
                  backgroundColor: 'secondary.main',
                  color: 'white',
                  borderRadius: '8px',
                  ml: 1,
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={handleColorClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            p: 2,
          },
        }}
      >
        <Grid container spacing={1} sx={{ width: 280 }}>
          {colorOptions.map((color) => (
            <Grid item key={color}>
              <Box
                onClick={() => handleColorSelect(color)}
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: color,
                  border: currentStyle.color === color ? '3px solid #1976d2' : '2px solid #e0e0e0',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'scale(1.2)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  },
                  transition: 'all 0.2s ease',
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Popover>

      {/* Thickness Picker Popover */}
      <Popover
        open={Boolean(thicknessAnchor)}
        anchorEl={thicknessAnchor}
        onClose={handleThicknessClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            p: 2,
          },
        }}
      >
        <Box sx={{ width: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Line Thickness
          </Typography>
          <Grid container spacing={1}>
            {thicknessPresets.map((thickness) => (
              <Grid item key={thickness}>
                <Box
                  onClick={() => handleThicknessSelect(thickness)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 12px',
                    backgroundColor: currentStyle.thickness === thickness ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: currentStyle.thickness === thickness ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    '&:hover': {
                      backgroundColor: currentStyle.thickness === thickness ? '#e3f2fd' : '#e8e8e8',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box
                    sx={{
                      width: thickness * 2,
                      height: 2,
                      backgroundColor: '#000',
                      borderRadius: 1,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {thickness}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      {/* Font Size Picker Popover */}
      <Popover
        open={Boolean(fontSizeAnchor)}
        anchorEl={fontSizeAnchor}
        onClose={handleFontSizeClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            p: 2,
          },
        }}
      >
        <Box sx={{ width: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Font Size
          </Typography>
          <Grid container spacing={1}>
            {fontSizePresets.map((size) => (
              <Grid item key={size}>
                <Box
                  onClick={() => handleFontSizeSelect(size)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    backgroundColor: currentStyle.fontSize === size ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: currentStyle.fontSize === size ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    '&:hover': {
                      backgroundColor: currentStyle.fontSize === size ? '#e3f2fd' : '#e8e8e8',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: Math.min(size / 2, 16),
                    }}
                  >
                    {size}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      {/* Shape Picker Popover */}
      <Popover
        open={Boolean(shapePickerAnchor)}
        anchorEl={shapePickerAnchor}
        onClose={handleShapeClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            p: 2,
          },
        }}
      >
        <Box sx={{ width: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Shape Type
          </Typography>
          <Grid container spacing={1}>
            {shapeOptions.map((option) => (
              <Grid item key={option.value}>
                <Box
                  onClick={() => handleShapeSelect(option.value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '12px',
                    backgroundColor: selectedShape === option.value ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: selectedShape === option.value ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    minWidth: 80,
                    '&:hover': {
                      backgroundColor: selectedShape === option.value ? '#e3f2fd' : '#e8e8e8',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {option.icon}
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      {/* Line Type Picker Popover */}
      <Popover
        open={Boolean(lineTypeAnchor)}
        anchorEl={lineTypeAnchor}
        onClose={handleLineTypeClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            p: 2,
            maxWidth: 300,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ width: 250 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Line Type
          </Typography>
          <Stack spacing={1}>
            {lineTypeOptions.map((option) => (
              <Box
                key={option.value}
                onClick={() => handleLineTypeSelect(option.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px',
                  backgroundColor: currentStyle.lineType === option.value ? '#e3f2fd' : '#f5f5f5',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: currentStyle.lineType === option.value ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: currentStyle.lineType === option.value ? '#e3f2fd' : '#e8e8e8',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60 }}>
                  {option.label}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace',
                    color: '#666',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.example}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default AnnotationToolbar; 